import Collapsible from "app/components/Collapsible/Collapsible";
import InputWithLabel from "app/components/FormInputs/components/InputWithLabel/InputWithLabel";
import { registrationInputMaxLength } from "app/utils/const";
import { ChangeEventHandler, MouseEventHandler } from "react";
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  IntlShape,
} from "react-intl";
import PersonalDetailsConfirmText from "./components/PersonalDetailsConfirmText/PersonalDetailsConfirmText";

type Props = {
  handleChange: Function;
  handleDateChange: ChangeEventHandler;
  active: boolean;
  onClickConfirm: MouseEventHandler;
  dateOfBirth: string;
  countryId: number;
  countries: any;
  nationalityId: number;
  nationalities: any;
  handleNationalityChange: ChangeEventHandler;
  handleCountryChange: ChangeEventHandler;
  errorMessage: string;
  city: string;
  intl: IntlShape;
  contactNumber: string;
};

const PersonalDetails: React.FC<Props> = props => {
  const {
    handleChange,
    handleDateChange,
    active,
    onClickConfirm,
    dateOfBirth,
    countryId,
    countries,
    nationalityId,
    nationalities,
    handleNationalityChange,
    handleCountryChange,
    errorMessage,
    contactNumber,
    intl,
  } = props;
  const I18n = defineMessages({
    firstName: {
      id: "home-firstname",
    },
    middleName: {
      id: "home-middlename",
    },
    lastName: {
      id: "home-lastname",
    },
    dateOfBirth: {
      id: "settings-birth",
    },
    nationality: {
      id: "home-nationality",
    },
    contactNumber: {
      id: "home-register-contact-number",
    },
    addressOne: {
      id: "registrations-address-line-1",
    },
    addressTwo: {
      id: "registrations-address-line-2",
    },
    addressThree: {
      id: "registrations-address-line-3",
    },
    city: {
      id: "registrations-city",
    },
    postCode: {
      id: "cashier-postcode",
    },
    country: {
      id: "registrations-country",
    },
  });

  const addressFields = [
    "addressOne",
    "addressTwo",
    "addressThree",
    "city",
    "postCode",
  ];
  const nameFields = ["firstName", "middleName", "lastName"];
  const requiredFields = [
    "firstName",
    "lastName",
    "addressOne",
    "city",
    "postCode",
    "dateOfBirth",
  ];

  return (
    <Collapsible
      onToggle={() => {
        handleChange("personalDetailsActive", !active);
      }}
      header={
        <div className="collapsible-header">
          <FormattedMessage id="home-register-personal-details" />
        </div>
      }
      content={
        <div className="collapsible-content">
          <div className="personal-details">
            <div className="personal-details__column">
              {nameFields.map((field: string, index: number) => {
                const fieldLabel = I18n[field];
                return (
                  <InputWithLabel
                    label={intl.formatMessage({ id: fieldLabel.id })}
                    inputId={field}
                    value={props[field]}
                    onValueChange={handleChange}
                    key={index}
                    required={requiredFields.includes(field)}
                    maxLength={registrationInputMaxLength.emailMaxLength}
                  />
                );
              })}
              <InputWithLabel
                label={intl.formatMessage({ id: "settings-birth" })}
                inputId={"dateOfBirth"}
                value={props.dateOfBirth}
                onValueChange={handleChange}
                type={"date"}
                required={requiredFields.includes("dateOfBirth")}
              />
              <div className="register-screen__form__dropdown personal-details__form__dropdown">
                <label className="text-input__label" htmlFor="nationality">
                  <FormattedMessage tagName="span" id="home-nationality" />
                  {"*"}
                </label>
                <select
                  className="form-control"
                  value={props.nationalityId}
                  onChange={handleNationalityChange}
                  id="nationality"
                >
                  {!nationalityId && (
                    <option value={0}>
                      <FormattedMessage id="home-register-select-nationality" />
                    </option>
                  )}
                  {nationalities &&
                    nationalities.map((country, index) => (
                      <option key={index} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                </select>
              </div>
              <InputWithLabel
                label={intl.formatMessage({ id: I18n.contactNumber.id })}
                inputId={"contactNumber"}
                value={props.contactNumber}
                onValueChange={handleChange}
                required={true}
                // asked API and max length number is what the DB expects
                maxLength={255}
              />
            </div>
            <div className="personal-details__column">
              {addressFields.map((field: string, index: number) => {
                const fieldLabel = I18n[field];
                return (
                  <InputWithLabel
                    label={intl.formatMessage({ id: fieldLabel.id })}
                    inputId={field}
                    value={props[field]}
                    onValueChange={handleChange}
                    key={index}
                    required={requiredFields.includes(field)}
                    maxLength={registrationInputMaxLength.addressInputMaxLength}
                  />
                );
              })}

              <div className="register-screen__form__dropdown personal-details__form__dropdown">
                <label className="text-input__label" htmlFor="country">
                  <FormattedMessage tagName="span" id="settings-country" />
                  {"*"}
                </label>
                <select
                  className="form-control"
                  value={countryId}
                  onChange={handleCountryChange}
                  id="country"
                >
                  {!countryId && (
                    <option value={0}>
                      <FormattedMessage id="home-register-select-country" />
                    </option>
                  )}
                  {countries &&
                    countries.map((country, index) => (
                      <option
                        key={index}
                        value={country.id}
                        data-countrycode={country.code}
                      >
                        {country.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </div>
          <div className="register-screen__form__messages">
            <PersonalDetailsConfirmText />
          </div>
          <div className="register-screen__form__error">{errorMessage}</div>
          <button
            type="submit"
            className="register-screen__form__submit-button"
            id="personal_details_submit_button"
            onClick={onClickConfirm}
          >
            <FormattedMessage
              tagName="span"
              id="home-register-confirm-continue"
            />
          </button>
        </div>
      }
      isActive={active}
      withArrows
    />
  );
};

export default injectIntl(PersonalDetails);
